/**
 * @module providers/CartProvider
 * @description React Context Provider for shopping cart management.
 * Handles cart state, item addition/removal, checkout flow, and synchronization with Netlify backend.
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { globalHistory } from '@reach/router';
import { apiCall } from '@/api/apiCall';
import { useIdentity } from './IdentityProvider';

const CartContext = React.createContext();

const debug = window.debugCart || false;

/**
 * @typedef BasicProps
 * @prop {string} className css className (*unused here*)
 * @prop {ReactNode} children children nodes
 */

/**
 * @typedef CartDiscount
 * @property {string} code discount code
 * @property {string} name name of discount
 * @property {number} deduction amount to deduct
 * @property {"percent" | "amount"} type discount type
 */

/**
 * @typedef FormatDataFromServerProps
 * @prop {Items[]} items cart items
 * @prop {number} total total cost
 * @prop {CartDiscount} discount discount amount
 * @prop {boolean} newOrder is new order? (**unused**)
 */

/**
 * @typedef FormatDataFromServerReturn
 * @prop {Items[]} items cart items
 * @prop {number} total total cost
 * @prop {CartDiscount} discount discount amount
 */

/**
 * @func formatDataFromServer
 * @param {FormatDataFromServerProps} arg data to format
 * @returns {FormatDataFromServerReturn} formatted/normalized data
 */
const formatDataFromServer = ({
  items = [],
  total = 0,
  discount = null,
  newOrder,
}) => ({
  items,
  total,
  discount,
});

/**
 * @typedef CartContextValue
 * @prop {Cart} cart cart data (*spread*)
 * @prop {boolean} isOpen if cart view is open
 * @prop {boolean} isCheckoutOpen is checkout view is open
 * @prop {boolean} isCheckoutDisabled if checkout is disabled via env config
 * @prop {boolean} isLocked is cart locked
 * @prop {React.SetStateAction<boolean>} setIsLocked set if cart is locked
 * @prop {number} addingCount number of items added to cart
 * @prop {React.SetStateAction<object>} setCart update `cart` to have new `data` (via `formatDataFromServer`
 * @prop {function} removeClientId function to remove discount code ("client id" / "authorization id")
 * @prop {function} open set cart to open
 * @prop {function} close set cart to close, set checkout to close
 * @prop {function} closeIfCheckoutIsNotOpen function to close cart if not in checkout
 * @prop {function} openCheckout set checkout to open
 * @prop {function} closeCheckout close checkout view
 * @prop {function} addItems add given items to cart
 * @prop {function} isItemLoading determines if given item is in array to be removed (`itemsBeingRemoved`)
 * @prop {function} removeItem remove given item
 * @prop {object|null} prefillData cached prefill data for checkout form
 * @prop {boolean} isPrefillLoading is prefill data currently loading
 * @prop {boolean} isPrefillReady prefill has loaded (success or error)
 */

/**
 * @typedef CartProviderContextProps
 * @prop {CartContextValue} value context value
 * @prop {ReactNode} children children nodes
 */

/**
 * @component CartProvider
 * @type {React.FC<BasicProps>}
 * @summary React Context Provider to provide cart items + status to child components
 * @param {BasicProps} props component props
 * @returns {ReactNode<CartProviderContextProps>} React Context provider
 */
const isCheckoutDisabled =
  process.env.REACT_APP_CHECKOUT_DISABLED === 'true';

const CartProvider = (props) => {
  const { isLoggedIn } = useIdentity();

  const [cart, _setCart] = useState(formatDataFromServer({}));

  /**
   * @func setCart
   * @param {Cart} data cart data
   * @summary updates `cart` object with given data + formatting
   *  - if `data.newOrder`, will reset cart data entirely
   * @returns {ReturnType<React.SetStateAction<Cart>>} return value from `_setCart`
   */
  const setCart = useCallback(
    (data = {}) => _setCart(formatDataFromServer(data.newOrder ? {} : data)),
    [],
  );

  const [isLocked, setIsLocked] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const checkoutActiveRef = useRef(false);

  /* If multiple items are being added at the same time, show multiple loading indicators */
  const [addingCount, setAddingCount] = useState(0);
  /* Show loading indicators directly on items being removed */
  const [itemsBeingRemoved, setItemsBeingRemoved] = useState({});

  /* Prefill data management for checkout form */
  const [prefillData, setPrefillData] = useState(null);
  const [isPrefillLoading, setIsPrefillLoading] = useState(false);
  const [isPrefillReady, setIsPrefillReady] = useState(false);

  /**
   * @func fetchCart
   * @async
   * @summary fetches cart data from netlify (via `get-cart`) and set local `cart` object with results
   *  - if new order AND checkout is not actively in progress, call `checkout-finish` and reload window
   *  - skips auto-finish when checkout is open to avoid racing with the explicit Checkout.js flow
   */
  const fetchCart = useCallback(async () => {
    try {
      const [data] = await apiCall('get-cart');
      debug &&
        console.log(
          '%ccartData fetched',
          'background:blue; color:white;',
          data,
        );

      setCart(data);
      if (data?.newOrder && !checkoutActiveRef.current) {
        const [newOrderData] = await apiCall('checkout-finish');
        if (newOrderData?.success) {
          window.location.reload();
        }
      }
    } catch (e) {
      debug && console.warn('fetchCart error', e);
    }
  }, [setCart]);

  /**
   * @func fetchPrefill
   * @async
   * @summary Prefetch checkout form data from get-prefill endpoint
   *  - fetches and caches prefill data for instant checkout form population
   *  - called when checkout is opened to minimize perceived latency
   */
  const fetchPrefill = useCallback(async () => {
    if (!isLoggedIn) return;

    setIsPrefillLoading(true);
    setIsPrefillReady(false);

    try {
      const [response] = await apiCall('get-prefill', {}, { method: 'POST' });

      if (response?.success && response?.data) {
        setPrefillData(response.data);
        debug && console.log(
          '%cprefill data cached',
          'background:purple; color:white;',
          response.data,
        );
      }
    } catch (e) {
      debug && console.warn('prefill fetch error', e);
    } finally {
      setIsPrefillLoading(false);
      setIsPrefillReady(true);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    /* If the user logs out, close the cart and checkout screen */
    if (isLoggedIn) {
      fetchCart();
    } else {
      setIsOpen(false);
      setIsCheckoutOpen(false);
      setIsLocked(false);
      setCart();
      setPrefillData(null);
      setIsPrefillReady(false);
    }
  }, [isLoggedIn, fetchCart, setCart]);

  useEffect(() => {
    checkoutActiveRef.current = isCheckoutOpen;
  }, [isCheckoutOpen]);

  useEffect(() => {
    // on location change, close the cart
    globalHistory.listen(() => {
      setIsOpen(false);
      setIsCheckoutOpen(false);
      setIsLocked(false);
    });
  }, []);

  debug && (window.cart = cart);

  /**
   * @func addItems
   * @async
   * @summary adds given items to cart
   *  - via `add-to-cart`
   *  - on successful response, update local object with data
   *    - also update item count (`addingCount`)
   *  - else display error
   * @param {Array<string | number>} itemIds ids of items to add
   */
  const addItems = useCallback(
    async (itemIds) => {
      debug &&
        console.log(
          `%caddToCart ${itemIds.length} itemIds:`,
          'background:#ffd040; color:#222;',
          itemIds,
        );

      const itemsCount = itemIds.length;
      setIsOpen(true);
      setAddingCount((prev) => prev + itemsCount);

      const [data, status] = await apiCall(
        'add-to-cart',
        { ids: JSON.stringify(itemIds) },
        { method: 'POST' },
      );

      if (status === 200) {
        setCart(data);
        debug &&
          console.log(
            '%ccartData fetched (after item added)',
            'background:blue; color:white;',
            data,
          );
      } else if (status === 409) {
        debug && console.warn('already added');
      } else if (status === 400) {
        const missingFieldsMessage = data?.msg;
        debug &&
          console.warn(
            `Could not add-to-cart because: ${missingFieldsMessage}`,
          );
      }

      setAddingCount((prev) => prev - itemsCount);
    },
    [setCart],
  );

  /**
   * @func removeItem
   * @async
   * @summary remove given `itemId` from cart
   *  - via `remove-from-cart`
   *  - on successful response, update local object with data
   *    - also update item count (`addingCount`)
   *  - else display error
   * @param {string|number} itemId id of item to remove
   */
  const removeItem = useCallback(
    async (itemId) => {
      debug &&
        console.log(
          '%cremoveItem itemId:',
          'background:#ffd040; color:#222;',
          itemId,
        );

      setItemsBeingRemoved((prev) => ({ ...prev, [itemId]: true }));

      const [data, status] = await apiCall(
        'remove-from-cart',
        { _id: itemId },
        { method: 'DELETE' },
      );

      if (status === 200) {
        debug &&
          console.log(
            '%ccartData fetched (after item removed)',
            'background:blue; color:white;',
            data,
          );
        setCart(data);
      } else if (status === 409) {
        debug && console.warn('already deleted');
      } else if (status === 400) {
        const missingFieldsMessage = data?.msg;
        debug &&
          console.warn(
            `Could not remove-from-cart because: ${missingFieldsMessage}`,
          );
      }

      setItemsBeingRemoved((prev) => ({ ...prev, [itemId]: false }));
    },
    [setCart],
  );

  /**
   * @func removeClientId
   * @async
   * @summary removes used "client id" / "authorization id" / "promo code id" from order
   *  - via `remove-authorization-id`
   *  - on success, update cart price
   *  - else, display error (in console)
   */
  const removeClientId = useCallback(async () => {
    setItemsBeingRemoved((prev) => ({ ...prev, discount: true }));

    const [data, status] = apiCall(
      'remove-authorization-id',
      { _id: cart.discount.id },
      { method: 'DELETE' },
    );

    if (status === 200) {
      const { items = [] } = cart;

      setCart({
        items,
        total: items.reduce((t, { price }) => t + price, 0),
      });
    } else if (data.msg) {
      debug &&
        console.warn('%cdata.msg', 'background:pink; color:black;', data.msg);
    }

    setItemsBeingRemoved((prev) => ({ ...prev, discount: false }));
  }, [cart, setCart]);

  const cartContextValue = useMemo(
    () => ({
      ...cart,
      isOpen,
      isCheckoutOpen,
      isCheckoutDisabled,
      setIsCheckoutOpen,
      isLocked,
      setIsLocked,
      addingCount,
      setCart,
      removeClientId,
      prefillData,
      isPrefillLoading,
      isPrefillReady,
      open: () => setIsOpen(true),
      close: () => {
        setIsOpen(false);
        setIsCheckoutOpen(false);
      },
      closeIfCheckoutIsNotOpen: () => !isCheckoutOpen && setIsOpen(false),
      openCheckout: () => {
        setIsCheckoutOpen(true);
        // Prefetch checkout form data when opening checkout
        fetchPrefill();
      },
      closeCheckout: () => setIsCheckoutOpen(false),
      addItems,
      isItemLoading: (itemId) => !!itemsBeingRemoved[itemId],
      removeItem,
    }),
    [
      cart,
      setCart,
      isOpen,
      setIsOpen,
      isCheckoutOpen,
      setIsCheckoutOpen,
      isLocked,
      setIsLocked,
      addItems,
      removeItem,
      removeClientId,
      itemsBeingRemoved,
      addingCount,
      prefillData,
      isPrefillLoading,
      isPrefillReady,
      fetchPrefill,
    ],
  );

  return (
    <CartContext.Provider value={cartContextValue}>
      {props.children}
    </CartContext.Provider>
  );
};

/**
 * @func useCart
 * @summary React Hook to hook into `CartContext` to get context value inside child components
 * @returns {React.Context<CartContext>} hook into `CartContext`
 */
export const useCart = () => useContext(CartContext);

export default CartProvider;
